<template>
    <Head title="Connection Details" />
    <BaseLayout gap="lg" no-top-padding content-class="pt-6 md:pt-22">
        <template #header>
            <BaseLink type="button" class="flex gap-2 items-center text-valence-grey-750 ml-6 md:ml-0 mt-4 w-fit" :href="connections_page_url">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i> <span class="body-small">Back</span>
            </BaseLink>
        </template>
        <!-- Chat Composer Section - Contained -->
        <div class="w-full max-w-[1200px] mx-auto">
            <div class="flex gap-6 items-center" :class="discoverEnabled ? 'flex-col w-full' : 'flex-col md:flex-row'">
                <div v-if="discoverEnabled" class="w-full">
                    <ConnectionDetailDiscoverHeader :full-name="connectionFullName" />
                </div>
                <div v-else class="flex gap-4 items-center md:shrink-0">
                    <CoachingModeMarble class="w-12 h-12" />
                    <h2 class="display-serif text-left">
                        <span class="fs-mask">Ready to dive deeper?</span>
                        <br />
                        Ask me about {{ connection.persona?.name?.split(" ")[0] || "this person" }}
                    </h2>
                </div>
                <div :class="`w-full ${discoverEnabled ? '' : 'max-w-[800px]'}`">
                    <ChatMessageControls
                        ref="chatMessageControls"
                        :location="CHAT_MESSAGE_CONTROLS_LOCATION.CONNECTION_DETAIL"
                        :is-disabled="isTranscribing"
                        :is-transcribing="isTranscribing"
                        is-paste-disabled
                        :document-upload-enabled="document_upload_enabled"
                        :default-text-input="true"
                        :is-sending="isSending"
                        :initial-value="discoverEnabled ? undefined : chatComposerText"
                        :placeholder="discoverEnabled ? placeholderText : undefined"
                        :user-message-count="0"
                        @text-send="handleTextSend"
                        @text-keydown="error = null"
                        @recording-complete="handleRecordingComplete" />
                </div>
            </div>
            <div v-if="discoverEnabled" class="mt-6">
                <DiscoverUseCasesDisplay
                    :use-cases="discoverUseCases"
                    :location="ChatUseCaseLocation.PEOPLE_DETAIL_DISCOVER_MODULE"
                    :grid-view="true"
                    @insight-hover="handleUseCaseHoverIn"
                    @insight-hover-end="handleUseCaseHoverOut"
                    @insight-clicked="handleUseCaseClicked" />
            </div>
        </div>

        <!-- Visual Separator -->
        <hr class="border-t border-valence-grey-300" />

        <!-- Public Profile and Memory Section -->
        <div class="py-4">
            <div class="flex flex-col gap-4">
                <!-- HRIS source badge when collaboration profile is linked -->
                <div v-if="public_profile_user_id && hris_data" class="flex items-center gap-2">
                    <SourceChip source="neutral" size="xs"> Synced from HR </SourceChip>
                    <StatusChip v-if="hris_data?.is_inactive" state="neutral" size="xs">Inactive</StatusChip>
                </div>
                <!-- Show public profile when linked -->
                <div
                    v-if="public_profile_user_id"
                    class="rounded-lgr focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-valence-primary-500"
                    data-testid="linked-profile-section"
                    tabindex="-1">
                    <PublicProfileSection
                        :target-user-id="public_profile_user_id"
                        :basic-info="computedBasicInfo"
                        :public-profile-enabled="public_profile_enabled"
                        :hris-data="hris_data"
                        :facet-fields="facetFields"
                        :is-inactive="!!hris_data?.is_inactive" />
                </div>

                <!-- Show basic info and link profile option when no profile is linked -->
                <template v-else>
                    <div class="flex flex-col gap-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <h3 class="scaffold-body-small">Profile</h3>
                                <SourceChip v-if="hris_data" source="neutral" size="xs">Synced from HR</SourceChip>
                                <StatusChip v-if="hris_data?.is_inactive" state="neutral" size="xs">Inactive</StatusChip>
                            </div>
                        </div>
                        <BasicInfoCard v-if="computedBasicInfo" :basic-info="computedBasicInfo" :hris-data="hris_data" :facet-fields="facetFields" :summary="connectionSummary" />
                        <hr v-if="public_profile_enabled" class="border-t border-valence-grey-200" />
                        <!-- Link Profile Button - Show after the inline search is dismissed -->
                        <BaseButton
                            v-if="public_profile_enabled && !showLinkProfileSearch"
                            ref="linkProfileButton"
                            theme="neutral"
                            size="sm"
                            class="flex items-center gap-2 w-fit"
                            @click="handleLinkProfile">
                            <i class="bi bi-plus-lg text-base/none" aria-hidden="true"></i>
                            Link Collaboration Profile
                        </BaseButton>
                        <PublicProfileSearch
                            v-if="public_profile_enabled && showLinkProfileSearch"
                            :connection="connection"
                            :invite-url="collaboration_profile_invite_url"
                            :auto-focus="focusLinkProfileSearch"
                            @dismiss="handleDismissProfileSearch" />
                    </div>
                </template>

                <!-- With a collaboration profile linked, BasicInfoCard sits inside a tab, so the summary
                     needs its own block to stay visible (BUG-921). -->
                <div v-if="connectionSummary && public_profile_user_id" class="flex flex-col gap-2">
                    <h3 class="scaffold-body-small">Summary</h3>
                    <div class="rounded-lgr border border-valence-grey-250 bg-white p-4">
                        <MarkdownText :text="connectionSummary" class="body-small text-valence-grey-800" />
                    </div>
                </div>

                <!-- Separator line -->
                <hr class="border-t border-valence-grey-200" />

                <!-- Memory Section -->
                <div class="flex flex-col gap-2">
                    <div class="flex items-center justify-between gap-4 h-11">
                        <h3 class="scaffold-body-small grow">Memory</h3>
                        <BaseButton theme="neutral" size="sm" class="flex items-center gap-2" @click="handleNoteAdd">
                            <i class="bi bi-journal-text text-base/none" aria-hidden="true"></i>
                            Add Notes
                        </BaseButton>
                    </div>
                    <template v-if="allMemoryItems.length > 0">
                        <div class="rounded-2xl border border-valence-grey-200 bg-white overflow-hidden">
                            <div class="md:overflow-y-auto md:max-h-75 pt-0 [&>*:last-child]:border-b-0">
                                <ConnectionContextRow v-for="item in allMemoryItems" :key="item.id" :item="item" />
                            </div>
                        </div>
                    </template>
                    <div v-else class="md:h-25">
                        <p class="body-small text-left text-valence-grey-600 pb-4">
                            No mentions or notes for this person.<br />Chat about them in our next session to see it here.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <!-- Edit, Unlink Profile, and Delete Person Buttons - Bottom Right -->
        <div class="flex flex-wrap items-center justify-end gap-2 md:gap-3 py-4">
            <BaseButton theme="neutral" size="sm" class="flex items-center gap-2" :disabled="!isReady" @click="handleEdit">
                <i class="bi bi-pen text-base/none" aria-hidden="true"></i>
                Edit Person
            </BaseButton>
            <BaseButton
                v-if="public_profile_user_id"
                theme="neutral"
                size="sm"
                class="flex items-center gap-2"
                :disabled="!isReady"
                @click="isConfirmUnlinkProfileDialogOpen = true">
                <i class="bi bi-link-45deg text-base/none" aria-hidden="true"></i>
                Unlink Collaboration Profile
            </BaseButton>
            <MergeConnectionsDialog :current-connection="connection" @merged="handleMerged">
                <template #trigger>
                    <BaseButton theme="neutral" size="sm" class="flex items-center gap-2" :disabled="!isReady">
                        <i class="bi bi-arrow-left-right text-base/none" aria-hidden="true"></i>
                        Merge Person
                    </BaseButton>
                </template>
            </MergeConnectionsDialog>
            <BaseButton theme="danger" size="sm" class="flex items-center gap-2" :disabled="!isReady" @click="isConfirmDeletePersonDialogOpen = true">
                <i class="bi bi-trash text-base/none" aria-hidden="true"></i>
                Delete Person
            </BaseButton>
        </div>
    </BaseLayout>
    <ConfirmationDialog
        v-model:open="isConfirmDeletePersonDialogOpen"
        title="Delete person?"
        text="Are you sure you want to delete this person? This action cannot be undone."
        confirm-label="Delete"
        @confirm="handleDeletePerson" />
    <BaseDialog v-model:open="isConfirmUnlinkProfileDialogOpen" title="Unlink Collaboration Profile?" show-close>
        <div class="p-6 pt-0 md:min-w-100 max-w-120 mx-auto flex flex-col gap-3">
            <h2 class="title-body">Unlink Collaboration Profile?</h2>
            <p class="body-small">Are you sure you want to unlink this collaboration profile from the connection?</p>
            <p v-if="showUnlinkProfileError" class="details text-red-600">There was an error unlinking the profile. Please try again.</p>
            <div class="flex justify-end pt-4">
                <BaseButton theme="primary" :disabled="isUnlinkingProfile" class="flex items-center justify-center gap-2 w-full md:w-auto" @click="handleUnlinkProfile">
                    <LoadingSpinner v-if="isUnlinkingProfile" class="text-white h-4 w-4" />
                    Unlink
                </BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script>
import DiscoverUseCasesDisplay from "~vue/ChatUseCaseEntrance/DiscoverUseCasesDisplay.vue";
import { ChatUseCaseLocation, isRolePlayUseCase } from "~vue/ChatUseCaseEntrance/utils.js";
import ConnectionDetailDiscoverHeader from "~vue/ConnectionDetail/ConnectionDetailDiscoverHeader.vue";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import { openUrl } from "~vue/utils";
export default { layout: LeftSidebarLayout };
</script>

<script setup>
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasicInfoCard from "~vue/components/BasicInfoCard.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import PublicProfileSection from "~vue/components/PublicProfileSection.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useConnectionActions } from "~vue/composables/useConnectionActions";
import { useTranscription } from "~vue/composables/useTranscription";
import ConnectionContextRow from "~vue/ConnectionDetail/ConnectionContextRow.vue";
import MergeConnectionsDialog from "~vue/Connections/MergeConnectionsDialog.vue";
import PublicProfileSearch from "~vue/Connections/PublicProfileSearch.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { useChatStore } from "~vue/stores/chatStore";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION } from "~vue/utils/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

const { $setEventContext } = inject("globalProperties");

const {
    connection,
    timeline,
    connections_page_url,
    event_context,
    document_upload_enabled,
    public_profile_enabled,
    people_detail_discover_use_cases,
    public_profile_user_id,
    public_profile_basic_info,
    hris_data,
    collaboration_profile_invite_url,
} = defineProps({
    connection: { type: Object, required: true },
    timeline: { type: Array, default: () => [] },
    connections_page_url: { type: String, required: true },
    event_context: { type: Object, required: true },
    document_upload_enabled: { type: Boolean, default: false },
    public_profile_enabled: { type: Boolean, default: false },
    people_detail_discover_use_cases: { type: Object, required: true },
    public_profile_user_id: { type: String, default: null },
    public_profile_basic_info: { type: Object, default: null },
    hris_data: { type: Object, default: null },
    collaboration_profile_invite_url: { type: String, default: "" },
});

const { getChatMessage, startChat, startRolePlay } = useConnectionActions();

const chatComposerText = computed(() => getChatMessage(connection.persona?.name));
const discoverUseCases = computed(() => people_detail_discover_use_cases.cases || []);
const discoverEnabled = computed(() => people_detail_discover_use_cases.enabled && discoverUseCases.value.length > 0);

const connectionFullName = computed(() => connection.persona?.name || "your connection");
const connectionSummary = computed(() => connection.connection?.summary || "");
const defaultPlacholderText = computed(() => `Ask Nadia anything about ${connectionFullName.value}`);
const placeholderText = ref(defaultPlacholderText.value);

// Compute basic info from connection if public_profile_basic_info is not available
const computedBasicInfo = computed(() => {
    if (public_profile_basic_info) {
        return public_profile_basic_info;
    }
    // Fallback to connection data
    return {
        full_name: connection.persona?.name || "",
        role: connection.connection?.job_title || null,
        team: null,
        level: null,
        organization: null,
    };
});

const facetFields = computed(() => connection.facet_values?.filter((fv) => fv.value) ?? []);

const isSending = ref(false);
const error = ref(null);
const { isTranscribing, transcribe } = useTranscription({ source: "ConnectionDetail", error });
const isReady = ref(false);
const showLinkProfileSearch = ref(true);
const focusLinkProfileSearch = ref(false);
const linkProfileButton = ref(null);
// Combine mentions and notes into a single list
const allMemoryItems = computed(() => timeline.filter((item) => item.type === "mention" || item.type === "note"));

const handleEdit = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_EDIT_BUTTON_CLICKED);
    router.visit(`/people/edit/${connection.id}/`);
};

const isConfirmDeletePersonDialogOpen = ref(false);
const { execute: deleteConnection, onFetchResponse } = useFetch(`/api/connections/${connection.id}/`, { immediate: false }).delete();
onFetchResponse(() => {
    router.visit(connections_page_url);
});
const handleDeletePerson = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_DELETE, { source: "connection_detail" });
    deleteConnection();
};

const isConfirmUnlinkProfileDialogOpen = ref(false);
const {
    execute: executeUnlinkProfile,
    error: unlinkProfileError,
    isFetching: isUnlinkingProfile,
} = useFetch(`/api/connections/${connection.id}/`, { immediate: false }).put({ public_profile: null }).json();
const showUnlinkProfileError = computed(() => !!unlinkProfileError.value && !isSessionEndedError(unlinkProfileError.value));
const handleUnlinkProfile = async () => {
    try {
        logUserInteraction(AmplitudeEventNames.CONNECTION_UNLINK_PUBLIC_PROFILE, {
            connection_id: connection.id,
            source: "my_people",
        });

        await executeUnlinkProfile();

        if (unlinkProfileError.value) {
            logError("Failed to unlink collaboration profile", unlinkProfileError.value);
        } else {
            isConfirmUnlinkProfileDialogOpen.value = false;
            // Reload the page to refresh the connection data
            router.reload({ only: ["connection", "public_profile_user_id", "public_profile_basic_info"] });
        }
    } catch (e) {
        logError("Unlink profile error", e);
    }
};

const handleMerged = ({ keptId }) => {
    // If the record being viewed survived the merge, refresh it; otherwise the
    // current connection was removed, so navigate to the surviving record.
    if (keptId === connection.id) {
        router.reload();
    } else {
        router.visit(`/people/${keptId}/`);
    }
};

const handleNoteAdd = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_ADD_CONTEXT_BUTTON_CLICKED);
    router.get(`/people/${connection.id}/note/`, {
        preserveState: true,
    });
};

const handleLinkProfile = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_PROFILE_BUTTON_CLICKED, {
        connection_id: connection.id,
        source: "my_people",
    });
    focusLinkProfileSearch.value = true;
    showLinkProfileSearch.value = true;
};

const handleDismissProfileSearch = async () => {
    showLinkProfileSearch.value = false;
    focusLinkProfileSearch.value = false;
    await nextTick();
    linkProfileButton.value?.$el?.focus();
};

const handleTextSend = async ({ content, source = "text" }) => {
    if (isSending.value) {
        return;
    }
    isSending.value = true;
    error.value = null;

    // Files in the store are a mix of UploadedFile (from /api/chat/document/upload)
    // and pasted-content temp objects (from ChatMessageControls.handlePaste).
    // The backend FileModel union accepts both shapes verbatim.
    const files = useChatStore.files.filter((f) => "id" in f || "content" in f);

    try {
        const redirectUrl = await startChat(connection, {
            message: content,
            files,
            logSource: source,
        });

        if (redirectUrl) {
            useChatStore.removeAllFiles();
            router.visit(redirectUrl);
        } else {
            throw new Error("No redirect URL received");
        }
    } catch (e) {
        error.value = "Something went wrong. Please try again.";
        logError(`Failed to start chat: ${JSON.stringify(e)}`);
    } finally {
        isSending.value = false;
    }
};

const handleRecordingComplete = async ({ blob, audioStats }) => {
    const transcript = await transcribe({ blob, audioStats });
    if (transcript) {
        handleTextSend({ content: transcript, source: "voice" });
    }
};

const handleUseCaseHoverIn = (useCase) => {
    placeholderText.value = useCase.description;
};

const handleUseCaseHoverOut = () => {
    placeholderText.value = defaultPlacholderText.value;
};

// Discover entrance conversation: materialize chat with variables and open URL
const discoverEntrancePayload = ref(null);
const {
    data: discoverEntranceData,
    execute: executeDiscoverEntrance,
    onFetchResponse: onDiscoverEntranceResponse,
} = useFetch("/api/connections/discover-entrance/conversation/", { immediate: false }).post(discoverEntrancePayload).json();

onDiscoverEntranceResponse(() => {
    if (discoverEntranceData.value?.coaching_session_url) {
        openUrl(discoverEntranceData.value.coaching_session_url);
    }
});

const handleUseCaseClicked = (use_case) => {
    if (isRolePlayUseCase(use_case)) {
        // For the roleplay chat entrance, open the existing roleplay config page
        startRolePlay(connection);
        return;
    }
    discoverEntrancePayload.value = {
        prompt_call_code: use_case.call_code,
        connection_id: connection.id,
    };
    executeDiscoverEntrance();
};

// Watch for profile linking to reset the search state
watch(
    () => public_profile_user_id,
    (newValue) => {
        showLinkProfileSearch.value = !newValue;
        focusLinkProfileSearch.value = false;
    },
);

onMounted(() => {
    $setEventContext(event_context);
    isReady.value = true;
});

onUnmounted(() => {
    $setEventContext({});
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
</style>
